// ─────────────────────────────────────────────────────────────────────────────
// Pure logic of the "student submissions" views (LOT3-P7, #635): French labels
// for the submission statuses, grouping absence presences into ranges of
// consecutive days, coverage of an absence by an existing justification, and
// count of the files submitted per exam. No Vue dependency — testable.
// ─────────────────────────────────────────────────────────────────────────────
import type {
  Presence,
  PresenceStatusDefinition,
  StudentSubmission,
  StudentSubmissionStatus,
} from '@/sdk/model';
import { SUBMISSION_KINDS } from '@/sdk/model';
import { MS_PER_DAY, extractDateFromISO, getSchoolYearStart, parseDateStringAsLocal } from '@/utils/date-utils';
import { formatDateString } from '@/utils/format';

/**
 * Fondation feature flag gating the whole student submissions vertical (backend
 * `StudentSubmissionsFeature.KEY`). Off by default: every submissions surface stays
 * hidden until an administrator enables the flag (/core/features).
 */
export const STUDENT_SUBMISSIONS_FEATURE = 'StudentSubmissions';

/** French presentation of the frozen contract's technical statuses (P6). */
export const SUBMISSION_STATUS_DISPLAY: Record<StudentSubmissionStatus, { label: string; color: string }> = {
  Pending: { label: 'En cours de dépôt', color: 'warning' },
  Ready: { label: 'Déposé', color: 'success' },
  Failed: { label: 'Échec du transfert', color: 'error' },
  DocumentDisposed: { label: "Retiré par l'archiviste", color: 'grey' },
};

export function submissionStatusLabel(status: string): string {
  return SUBMISSION_STATUS_DISPLAY[status as StudentSubmissionStatus]?.label ?? status;
}

export function submissionStatusColor(status: string): string {
  return SUBMISSION_STATUS_DISPLAY[status as StudentSubmissionStatus]?.color ?? 'grey';
}

/** A submission still editable by the student: its files can be added/removed (P6 contract: Pending only). */
export function canEditSubmissionFiles(submission: Pick<StudentSubmission, 'status'>): boolean {
  return submission.status === 'Pending';
}

/** A submission deletable by the student (P7: Pending or Ready). */
export function canDeleteSubmission(submission: Pick<StudentSubmission, 'status'>): boolean {
  return submission.status === 'Pending' || submission.status === 'Ready';
}

/** Presence status keys marked as an "absence" in the definitions. */
export function absenceStatusKeys(definitions: PresenceStatusDefinition[]): Set<string> {
  return new Set(definitions.filter((d) => d.isAbsence).map((d) => d.key));
}

/**
 * Start of the "recent absences" window: the current school year start —
 * except between August 1st and the actual start (4th Monday of August),
 * where that start is still in the future and would make the window empty;
 * fall back to the previous school year instead.
 */
export function absenceWindowStart(reference: Date = new Date()): Date {
  const start = getSchoolYearStart(reference);
  return start > reference
    ? getSchoolYearStart(new Date(reference.getFullYear(), 6, 1))
    : start;
}

/** One absence day of a frequentation (the presences of the same day are merged). */
export interface AbsenceDay {
  frequentationId: string;
  /** « yyyy-MM-dd ». */
  date: string;
  presenceIds: string[];
}

/** A range of consecutive absence days of one frequentation. */
export interface AbsenceRange {
  frequentationId: string;
  /** « yyyy-MM-dd », inclusif. */
  startDate: string;
  /** « yyyy-MM-dd », inclusif. */
  endDate: string;
  presenceIds: string[];
}

type PresenceLike = Pick<Presence, 'id' | 'date' | 'frequentationId' | 'status'>;

/**
 * Reduces the presences to absence days: filters by absence status, normalizes
 * the date, merges the periods of the same day and sorts by date.
 */
export function toAbsenceDays(presences: PresenceLike[], absenceKeys: Set<string>): AbsenceDay[] {
  const byKey = new Map<string, AbsenceDay>();
  for (const presence of presences) {
    if (!presence.status || !absenceKeys.has(presence.status)) continue;
    const date = extractDateFromISO(presence.date);
    const key = `${presence.frequentationId}|${date}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.presenceIds.push(presence.id);
    } else {
      byKey.set(key, { frequentationId: presence.frequentationId, date, presenceIds: [presence.id] });
    }
  }
  return [...byKey.values()].sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * True when an active justification already covers this absence day.
 * Only completed submissions count (Ready, or DocumentDisposed — the submission
 * did exist): an abandoned Pending or a Failed one leaves the absence to justify,
 * and a new start on the same anchor replaces the Pending on the server side.
 */
export function isAbsenceDayCovered(day: AbsenceDay, submissions: StudentSubmission[]): boolean {
  return submissions.some((s) => {
    if (s.archived) return false;
    if (s.status !== 'Ready' && s.status !== 'DocumentDisposed') return false;
    if (s.frequentationId !== day.frequentationId) return false;
    if (day.presenceIds.some((id) => s.presenceIds?.includes(id))) return true;
    return !!s.absenceStartDate && !!s.absenceEndDate
      && s.absenceStartDate <= day.date && day.date <= s.absenceEndDate;
  });
}

/** Groups the absence days into ranges of consecutive CALENDAR days, per frequentation. */
export function groupAbsenceRanges(days: AbsenceDay[]): AbsenceRange[] {
  const byFrequentation = new Map<string, AbsenceDay[]>();
  for (const day of days) {
    const list = byFrequentation.get(day.frequentationId) ?? [];
    list.push(day);
    byFrequentation.set(day.frequentationId, list);
  }

  const ranges: AbsenceRange[] = [];
  for (const [frequentationId, list] of byFrequentation) {
    const sorted = [...list].sort((a, b) => a.date.localeCompare(b.date));
    let current: AbsenceRange | null = null;
    for (const day of sorted) {
      if (current && isNextCalendarDay(current.endDate, day.date)) {
        current.endDate = day.date;
        current.presenceIds.push(...day.presenceIds);
      } else {
        current = { frequentationId, startDate: day.date, endDate: day.date, presenceIds: [...day.presenceIds] };
        ranges.push(current);
      }
    }
  }
  return ranges;
}

function isNextCalendarDay(previous: string, next: string): boolean {
  const diff = parseDateStringAsLocal(next).getTime() - parseDateStringAsLocal(previous).getTime();
  return Math.round(diff / MS_PER_DAY) === 1;
}

/**
 * The absence ranges still to justify, most recent first: absence days not
 * covered by an active justification, grouped into ranges.
 */
export function uncoveredAbsenceRanges(
  presences: PresenceLike[],
  absenceKeys: Set<string>,
  submissions: StudentSubmission[],
): AbsenceRange[] {
  const days = toAbsenceDays(presences, absenceKeys).filter((d) => !isAbsenceDayCovered(d, submissions));
  return groupAbsenceRanges(days).sort((a, b) => b.startDate.localeCompare(a.startDate));
}

/** Number of files submitted per exam (paperclip badge) — active submissions only. */
export function countSubmissionFilesByExam(submissions: StudentSubmission[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const s of submissions) {
    if (s.archived || !s.examId) continue;
    counts[s.examId] = (counts[s.examId] ?? 0) + s.files.length;
  }
  return counts;
}

/** « 2026-08-03 » or « du 2026-08-01 au 2026-08-03 » (inclusive range). */
export function formatAbsenceRange(startDate: string, endDate: string): string {
  if (startDate === endDate) return formatDateString(startDate);
  return `du ${formatDateString(startDate)} au ${formatDateString(endDate)}`;
}

// ── Vues personnel (LOT3-P8, #636) ───────────────────────────────────────────

/**
 * The active submissions covering at least one of the given presences, from the
 * response of the batch GET /api/Presences/Submissions (indexed by presence id).
 * Deduplicated (one justification often covers several presences), recent first.
 */
export function submissionsForPresenceIds(
  byPresence: Record<string, StudentSubmission[]> | undefined,
  presenceIds: string[],
): StudentSubmission[] {
  if (!byPresence) return [];
  const byId = new Map<string, StudentSubmission>();
  for (const presenceId of presenceIds) {
    for (const submission of byPresence[presenceId] ?? []) {
      if (submission.archived) continue;
      byId.set(submission.id, submission);
    }
  }
  return [...byId.values()]
    .sort((a, b) => (b.createdDate ?? '').localeCompare(a.createdDate ?? ''));
}

/** A "submission kind" group within a school year. */
export interface SubmissionKindGroup {
  /** Technical key (SUBMISSION_KINDS, or an unknown key relayed as is). */
  kind: string;
  /** French display label. */
  label: string;
  submissions: StudentSubmission[];
}

/** The submissions of a school year, grouped by kind. */
export interface SubmissionYearGroup {
  schoolYear: string;
  kinds: SubmissionKindGroup[];
}

/**
 * Groups a student's active submissions by school year (most recent first) then
 * by kind (canonical order of SUBMISSION_KINDS, unknown kinds last) — the
 * structure of the « Documents » tab of the student record.
 */
export function groupSubmissionsByYearAndKind(submissions: StudentSubmission[]): SubmissionYearGroup[] {
  const byYear = new Map<string, StudentSubmission[]>();
  for (const submission of submissions) {
    if (submission.archived) continue;
    const list = byYear.get(submission.schoolYear) ?? [];
    list.push(submission);
    byYear.set(submission.schoolYear, list);
  }

  const knownKinds = SUBMISSION_KINDS.map((k) => k.value as string);
  return [...byYear.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([schoolYear, yearSubmissions]) => {
      const kindKeys = [
        ...knownKinds.filter((kind) => yearSubmissions.some((s) => s.kind === kind)),
        ...[...new Set(yearSubmissions.map((s) => s.kind))]
          .filter((kind) => !knownKinds.includes(kind))
          .sort((a, b) => a.localeCompare(b)),
      ];
      const kinds = kindKeys.map((kind) => ({
        kind,
        label: SUBMISSION_KINDS.find((k) => k.value === kind)?.label ?? kind,
        submissions: yearSubmissions
          .filter((s) => s.kind === kind)
          .sort((a, b) => (b.createdDate ?? '').localeCompare(a.createdDate ?? '')),
      }));
      return { schoolYear, kinds };
    });
}
